import { Box, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: SectionBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const SectionBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const addBlock = () => {
        const newBlocks = [...(block.content.blocks || [])];
        newBlocks.push({
            id: uuidv4(),
            type: PageBlockType.TEXT,
            order: newBlocks.length,
            content: { html: "<p>Enter text here...</p>" },
        });
        
        onChange({
            ...block.content,
            blocks: newBlocks,
        });
    };

    const removeBlock = (blockIndex: number) => {
        const newBlocks = [...block.content.blocks];
        newBlocks.splice(blockIndex, 1);
        
        onChange({
            ...block.content,
            blocks: newBlocks,
        });
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Stack spacing={2}>
                        <Typography variant="h6">Section</Typography>
                        
                        <TextField
                            label="Section Title (optional)"
                            value={block.content.title || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    title: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        
                        <TextField
                            label="Background Color (optional)"
                            value={block.content.backgroundColor || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    backgroundColor: e.target.value,
                                })
                            }
                            placeholder="#f5f5f5 or transparent"
                            fullWidth
                            size="small"
                        />
                        
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: block.content.backgroundColor || "#f9f9f9" }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Section Content
                            </Typography>
                            
                            <Stack spacing={1}>
                                {block.content.blocks?.map((nestedBlock, blockIndex) => (
                                    <Paper key={nestedBlock.id} sx={{ p: 1, bgcolor: "white" }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                {nestedBlock.type} block
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => removeBlock(blockIndex)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                ))}
                                
                                <IconButton
                                    size="small"
                                    onClick={addBlock}
                                    sx={{ alignSelf: "center" }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Stack>
                        </Paper>
                        
                        <Typography variant="caption" color="text.secondary">
                            Note: Nested blocks will use simplified rendering. Sections are useful for grouping related content with a background.
                        </Typography>
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default SectionBlockEditor;
